import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import type { OnboardingPipelineSummaryDto } from '../pages/vms/onboardingPipelineStatus';
import {
  buildServiceJourneyStepUi,
  buildServiceJourneyStepUiFromPipeline,
  resolveServiceJourneyStage,
  type VmsServiceJourneyStepUi,
  type VmsServiceJourneyStage,
} from '../pages/vms/vmsServiceJourneyModel';
import {
  fetchOnboardingPipelineStatusCached,
  resolveRegisteredVendorIdForInitiationCached,
} from '../utils/onboardingPipelineStatusCache';

function parsePositiveInt(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isInteger(raw) && raw > 0) return raw;
  if (typeof raw === 'string' && /^\d+$/.test(raw.trim())) return parseInt(raw.trim(), 10);
  return null;
}

function registeredVendorIdFromPath(pathname: string): number | null {
  const m = pathname.match(/^\/vms\/onboarding\/vendors\/(\d+)/);
  return m ? parsePositiveInt(m[1]) : null;
}

export function useVmsServiceJourneyStepUi(options: {
  registeredVendorId?: number | null;
  vendorInitiationId?: number | null;
  initiationApproved?: boolean;
}): Record<VmsServiceJourneyStage, VmsServiceJourneyStepUi> {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [pipelineSummary, setPipelineSummary] = useState<OnboardingPipelineSummaryDto | null>(null);

  const explicitRegId = parsePositiveInt(options.registeredVendorId ?? null);
  const regIdFromUrl =
    registeredVendorIdFromPath(location.pathname) ??
    parsePositiveInt(searchParams.get('vms_registered_vendor_id'));
  const vendorInitiationId = parsePositiveInt(options.vendorInitiationId ?? null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      let regId = explicitRegId ?? regIdFromUrl;
      if (regId == null && vendorInitiationId != null) {
        regId = await resolveRegisteredVendorIdForInitiationCached(vendorInitiationId);
      }
      if (regId == null || regId < 1) {
        if (!cancelled) setPipelineSummary(null);
        return;
      }
      const summary = await fetchOnboardingPipelineStatusCached(regId);
      if (!cancelled) setPipelineSummary(summary);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [explicitRegId, regIdFromUrl, vendorInitiationId]);

  return useMemo(() => {
    const fallbackCurrentStage = resolveServiceJourneyStage({
      routePathname: location.pathname,
      initiationApproved: options.initiationApproved,
    });
    if (pipelineSummary) {
      return buildServiceJourneyStepUiFromPipeline(pipelineSummary, {
        initiationApproved: options.initiationApproved,
        fallbackCurrentStage,
      });
    }
    return buildServiceJourneyStepUi(fallbackCurrentStage);
  }, [pipelineSummary, location.pathname, options.initiationApproved]);
}
